import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Button } from "devextreme-react/button";
import ContextMenu from "devextreme-react/context-menu";
import Popup from "devextreme-react/popup";
import Report from "../../../../../reports/components/Report";
import routes from "../../../../../assets/apiRoutes.json";
import { getReportMenu } from "../../../../../services/ReportGroupService";

// The OM report groups come from the DB (same source as the sidebar). Cached so
// multiple Print buttons share one call.
let cachedMenuPromise = null;
function fetchOmReportMenu(accessToken) {
  if (!cachedMenuPromise) {
    cachedMenuPromise = getReportMenu("OM", accessToken).catch((err) => {
      cachedMenuPromise = null;
      console.log(err, "Error getting OM report menu for print menu");
      return [];
    });
  }
  return cachedMenuPromise;
}

// Role permissions (OMReports) - cached across Print buttons.
let cachedPermissionsPromise = null;
function fetchOmReportPermissions(role, accessToken) {
  if (!cachedPermissionsPromise) {
    cachedPermissionsPromise = axios
      .get(`${window._env_.REACT_APP_AUTH_URL}/${routes.idp.GetPermissionsByRole}?roleName=${role}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => res.data)
      .catch((err) => {
        cachedPermissionsPromise = null;
        console.log(err, "Error getting role permissions for print menu");
        return null;
      });
  }
  return cachedPermissionsPromise;
}

// Build the ContextMenu data source from the DB groups, gated by permission.
// Only compiled reports (ABRUNPROGRAM) are runnable per-order from here.
function buildMenuItems(menu, omReports) {
  const allowed = (p) => !p || (omReports && omReports.includes(p));
  const toLeaf = (r) => ({
    text: r.Description || r.Name || r.FormName || r.ReportKey,
    report: {
      name: r.Name || r.FormName || r.ReportKey, // load the report by its Name
      fullName: r.Parameter,
      title: r.Description || r.Name || r.FormName || r.ReportKey,
    },
  });
  const result = [];
  (menu || []).forEach((group, gi) => {
    // Permission gating is per report item only (groups carry no permission).
    const reports = (group.ReportGroupItems || []).filter((r) => r.SourceTableName !== "ABLAYOUT" && allowed(r.Permission));
    if (reports.length === 0) return;
    if (!group.GroupName) {
      reports.forEach((r) => result.push(toLeaf(r)));
    } else {
      result.push({ key: `g_${gi}`, text: group.GroupName, items: reports.map(toLeaf) });
    }
  });
  return result;
}

let printMenuCounter = 0;

const PrintReportMenu = (props) => {
  const {
    selectedRecord,
    recordNumberField = "recordNumber",
    text = "Print",
    className = "gridTopButton",
    disabled = false,
  } = props;

  const buttonIdRef = useRef(`omPrintReportBtn_${++printMenuCounter}`);
  const contextMenuRef = useRef(null);
  const [activeReport, setActiveReport] = useState(null);
  const [reportMenu, setReportMenu] = useState([]);
  const [permissions, setPermissions] = useState(null);

  const role = props.oidc?.user?.profile?.role;
  const accessToken = props.oidc?.user?.access_token;

  useEffect(() => {
    let mounted = true;
    fetchOmReportMenu(accessToken).then((data) => {
      if (mounted) setReportMenu(Array.isArray(data) ? data : []);
    });
    fetchOmReportPermissions(role, accessToken).then((data) => {
      if (mounted) setPermissions(data);
    });
    return () => {
      mounted = false;
    };
  }, [role, accessToken]);

  const menuItems = useMemo(
    () => buildMenuItems(reportMenu, permissions?.OMReports),
    [reportMenu, permissions]
  );

  const recordNumber =
    selectedRecord && recordNumberField ? selectedRecord[recordNumberField] : null;

  const showMenu = useCallback(() => {
    if (contextMenuRef.current) {
      contextMenuRef.current.instance().show();
    }
  }, []);

  const onItemClick = useCallback((e) => {
    const itemData = e.itemData;
    if (itemData && itemData.report) {
      setActiveReport(itemData.report);
    }
  }, []);

  const closeReport = useCallback(() => setActiveReport(null), []);

  const initialFilters = recordNumber
    ? [{ fieldName: "KEYSEQUENCE", displayLabel: "Record #", value: recordNumber }]
    : [];

  return (
    <React.Fragment>
      <Button
        id={buttonIdRef.current}
        className={className}
        text={text}
        type="default"
        disabled={disabled}
        onClick={showMenu}
      />
      <ContextMenu
        ref={contextMenuRef}
        dataSource={menuItems}
        target={`#${buttonIdRef.current}`}
        showEvent=""
        position={{ my: "top left", at: "bottom left", of: `#${buttonIdRef.current}` }}
        onItemClick={onItemClick}
      />
      {activeReport && (
        <Popup
          visible={true}
          onHiding={closeReport}
          title={activeReport.title}
          showTitle={true}
          showCloseButton={true}
          width="90%"
          height="90%"
          dragEnabled={true}
          resizeEnabled={true}
          contentRender={() => (
            <Report
              key={activeReport.name}
              Name={activeReport.name}
              FullName={activeReport.fullName}
              Title={activeReport.title}
              initialFilters={initialFilters}
            />
          )}
        />
      )}
    </React.Fragment>
  );
};

function mapStateToProps(state) {
  return {
    oidc: state.oidc,
  };
}

export default connect(mapStateToProps)(PrintReportMenu);
